/**
 * A API espera o telefone como dígitos com código do país, sem máscara:
 * `5519999999999`. A UI trabalha com o formato brasileiro `(19) 9 9999-9999`.
 */

const BRAZIL_COUNTRY_CODE = "55";

/** Mesma regra do backend (`IsEmailOrPhone`): dígitos com DDI, `+` opcional. */
const E164_PHONE_REGEX = /^\+?[1-9]\d{7,14}$/;

/** Mantém só os dígitos. */
function onlyDigits(value: string): string {
  return value.replace(/\D/g, "");
}

/**
 * Converte o telefone digitado para o formato da API (`5519999999999`).
 * Assume Brasil quando o código do país não foi informado — a UI só oferece
 * o fuso de Brasília e a máscara nacional.
 */
export function toApiPhone(value: string): string {
  const digits = onlyDigits(value);
  if (!digits) return "";

  // 12 ou 13 dígitos já incluem o 55 (DDI + DDD + 8 ou 9 dígitos).
  if (digits.startsWith(BRAZIL_COUNTRY_CODE) && digits.length >= 12) {
    return digits;
  }

  return `${BRAZIL_COUNTRY_CODE}${digits}`;
}

/**
 * Encontra o telefone dentro de `recipients`.
 *
 * A lista mistura e-mails e telefones, e o backend insere o e-mail do usuário
 * logado como primeiro item — por isso não dá para assumir `recipients[0]`.
 */
export function findPhoneRecipient(recipients?: string[]): string | undefined {
  return recipients?.find((recipient) => E164_PHONE_REGEX.test(recipient));
}

/**
 * Caminho inverso, para exibir o valor que veio da API no input.
 * Devolve `(19) 9 9999-9999` para celular e `(19) 9999-9999` para fixo.
 */
export function fromApiPhone(value: string): string {
  let digits = onlyDigits(value);
  if (!digits) return "";

  if (digits.startsWith(BRAZIL_COUNTRY_CODE) && digits.length >= 12) {
    digits = digits.slice(BRAZIL_COUNTRY_CODE.length);
  }

  const areaCode = digits.slice(0, 2);
  const rest = digits.slice(2);

  if (rest.length === 9) {
    return `(${areaCode}) ${rest[0]} ${rest.slice(1, 5)}-${rest.slice(5)}`;
  }
  if (rest.length === 8) {
    return `(${areaCode}) ${rest.slice(0, 4)}-${rest.slice(4)}`;
  }

  // Formato inesperado: devolve o que veio, sem inventar máscara.
  return value;
}
